<template>
  <div class="venue-page">
    <div v-if="!showAdminAuth" class="venue-nav">
      <PublicSiteNav />
    </div>
    <div class="venue-container">
    <!-- Admin Authentication Modal -->
    <div v-if="showAdminAuth" class="auth-modal">
      <AdminAuth @authenticated="handleAdminAuth" />
    </div>

    <!-- Main Content -->
    <div v-else>
      <div v-if="isLoading && !venue" class="loading">Loading venue…</div>
      <div v-else-if="!venue" class="venue-unavailable" role="status">
        <p>This comedy night is not open for applications (it may have been archived).</p>
      </div>

      <template v-else-if="venue">
        <!-- Venue Header -->
        <div class="venue-header">
          <div class="venue-logo-header">
            <img :src="logoFor(venue)" :alt="venue.venue_name + ' logo'" />
          </div>
          <h1>{{ venue.venue_name }}</h1>
          <p class="venue-info">
            <span class="day">{{ dayLabel }}</span>
            <span class="location">📍 {{ venue.venue_location }}</span>
          </p>
          <p v-if="venue.address && venue.google_maps_url" class="venue-address">
            <span v-if="showMapPin">📍 </span>
            <a :href="venue.google_maps_url" target="_blank" rel="noopener noreferrer">{{ venue.address }}</a>
          </p>
          <p v-if="venue.description" class="venue-description">{{ venue.description }}</p>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading">Loading available dates...</div>

        <!-- Event Selection -->
        <div v-else-if="events.length > 0">
          <h2>{{ datesHeading }}</h2>
          <form @submit.prevent="handleApply">
            <ul class="dates-list">
              <li v-for="event in events" :key="event.id">
                <label>
                  <input type="checkbox" :value="event.id" v-model="selectedEvents" />
                  {{ event.event_name }} - {{ formatDateSuffix(event.event_date) }}
                </label>
              </li>
            </ul>
            <div class="button-container">
              <button type="submit" :disabled="selectedEvents.length === 0">
                Apply for {{ selectedEvents.length }} {{ selectedEvents.length === 1 ? 'Date' : 'Dates' }}
              </button>
            </div>
          </form>
        </div>

        <!-- No Events -->
        <div v-else class="no-events">
          <p>No upcoming dates available for this venue at the moment.</p>
          <p>Check back soon!</p>
        </div>
      </template>

      <!-- Application Form Modal -->
      <div v-if="showForm">
        <ApplicationForm :eventIds="selectedEvents" @close="handleFormClose" />
      </div>
    </div>
    </div>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — F1 (archived venue: no apply UI; `archived_at` check for auth users) */
import { supabase } from '../supabaseClient';
import ApplicationForm from './ApplicationForm.vue';
import AdminAuth from './AdminAuth.vue';
import { generateVenueDates, filterStaleEvents } from '../utils/dateGeneration';
import { applyDatesHeading } from '../utils/bookingSpot.js';
import { venueDayLabel } from '../utils/venueDisplay';
import { applyVenueNightTitle } from '../utils/documentTitle.js';
import { venueLogoSrc } from '../utils/venueLogo.js';
import PublicSiteNav from './PublicSiteNav.vue';

export default {
  name: 'VenueEventList',
  components: {
    ApplicationForm,
    AdminAuth,
    PublicSiteNav,
  },
  props: {
    venueSlug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      venue: null,
      events: [],
      selectedEvents: [],
      showForm: false,
      isLoading: true,
      errorMessage: '',
      showAdminAuth: false,
      pendingRedirect: null,
    };
  },
  computed: {
    dayLabel() {
      return this.venue ? venueDayLabel(this.venue) : '';
    },
    datesHeading() {
      return applyDatesHeading(this.venue);
    },
    showMapPin() {
      return !/zoom\.us\/download/i.test(this.venue?.google_maps_url || '');
    },
  },
  created() {
    // Check if admin authentication is required
    if (this.$route.query.auth === 'required') {
      this.showAdminAuth = true;
      this.pendingRedirect = this.$route.query.redirect;
    } else {
      this.fetchData();
    }
  },
  methods: {
    async fetchData() {
      try {
        this.isLoading = true;

        // 1. Fetch venue (anon: archived / hidden venues return no row — RLS)
        const { data: venueData, error: venueError } = await supabase
          .from('venues')
          .select('*')
          .eq('venue_slug', this.venueSlug)
          .maybeSingle();

        if (venueError) throw venueError;
        if (!venueData) {
          this.venue = null;
          this.events = [];
          return;
        }
        if (venueData.archived_at) {
          this.venue = null;
          this.events = [];
          return;
        }
        this.venue = venueData;
        if (this.$route.name === 'VenueNight') applyVenueNightTitle(venueData.venue_name);

        // 2. Fetch data using RPC
        const { data, error } = await supabase.rpc('get_event_data');
        if (error) throw error;

        const blackoutSet = new Set(data.blackout_dates?.map(bd => bd.date) || []);
        const allEvents = data.events || [];
        const configData = data.config;

        // Filter existing events for this venue
        let existingEvents = allEvents.filter(event => event.venue_id === this.venue.id);

        // Also filter by venue start_date if set
        if (this.venue.start_date) {
          const venueStartDate = new Date(this.venue.start_date);
          existingEvents = existingEvents.filter(event => {
            const eventDate = new Date(event.event_date);
            return eventDate >= venueStartDate;
          });
        }

        // Remove stale events on the old day after a day-of-week transition
        existingEvents = filterStaleEvents(existingEvents, this.venue);

        const horizonMonths = configData?.horizon_months || 6;

        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + horizonMonths);

        let startDate = new Date();
        startDate.setHours(0, 0, 0, 0);

        if (this.venue.start_date) {
          const venueStartDate = new Date(this.venue.start_date);
          venueStartDate.setHours(0, 0, 0, 0);
          if (venueStartDate > startDate) {
            startDate = venueStartDate;
          }
        }

        const existingEventNames = new Set(existingEvents.map(event => event.event_name));

        const generatedEvents = generateVenueDates({
          venue: this.venue,
          startDate,
          endDate,
          blackoutDates: blackoutSet,
          existingEventNames,
        });

        // Combine and sort events
        this.events = [...existingEvents, ...generatedEvents]
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

      } catch (err) {
        console.error('Unexpected error:', err);
        this.errorMessage = 'An unexpected error occurred while fetching events.';
      } finally {
        this.isLoading = false;
      }
    },

    handleAdminAuth() {
      this.showAdminAuth = false;
      if (this.pendingRedirect) {
        this.$router.push(this.pendingRedirect);
      } else {
        this.fetchData();
      }
    },

    formatDateSuffix(dateStr) {
      return dateStr.split('T')[0].split('-').reverse().join('/');
    },

    async handleApply() {
      // Create actual events for any generated ones
      const selectedGenerated = this.selectedEvents
        .filter(id => typeof id === 'string' && id.startsWith('generated_'))
        .map(id => {
          const dateStr = id.replace('generated_', '');
          const event = this.events.find(e => e.id === id);
          return {
            event_date: dateStr,
            event_name: event.event_name,
            venue_id: this.venue.id
          };
        });

      if (selectedGenerated.length > 0) {
        // Check for existing events
        const { data: existingEvents, error: checkError } = await supabase
          .from('events')
          .select('id, event_date, event_name, venue_id')
          .eq('venue_id', this.venue.id)
          .in('event_date', selectedGenerated.map(e => e.event_date));

        if (checkError) {
          console.error('Error checking for existing events:', checkError);
          return;
        }

        // Filter out existing
        const eventsToCreate = selectedGenerated.filter(newEvent =>
          !existingEvents?.some(existingEvent =>
            existingEvent.event_date === newEvent.event_date &&
            existingEvent.event_name === newEvent.event_name
          )
        );

        // Create new events using upsert to prevent race condition duplicates
        for (const event of eventsToCreate) {
          const { error } = await supabase
            .from('events')
            .upsert(event, { 
              onConflict: 'event_date,venue_id', 
              ignoreDuplicates: true 
            });

          if (error) {
            console.error('Error creating event:', error);
            continue;
          }
        }
      }

      // Get real event IDs
      const realEventIds = this.selectedEvents
        .filter(id => typeof id !== 'string' || !id.startsWith('generated_'));

      // Get IDs for newly created events
      const { data: newEvents } = await supabase
        .from('events')
        .select('id')
        .eq('venue_id', this.venue.id)
        .in('event_date', selectedGenerated.map(e => e.event_date));

      if (newEvents) {
        realEventIds.push(...newEvents.map(e => e.id));
      }

      this.selectedEvents = [...new Set(realEventIds)];

      // Refresh events so generated events are replaced with real DB records
      // This keeps checkboxes in sync with the new real event IDs
      await this.fetchData();

      // Re-select the real event IDs after refresh
      this.selectedEvents = [...new Set(realEventIds)];
      this.showForm = true;
    },

    handleFormClose() {
      this.showForm = false;
    },

    logoFor: venueLogoSrc,
  },
};
</script>

<style scoped>
.venue-page {
  min-height: 100vh;
  background: #fff;
}

.venue-nav {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.venue-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.venue-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.venue-logo-header {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.venue-logo-header img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.venue-header h1 {
  margin: 0 0 15px 0;
  font-size: 32px;
}

.venue-info {
  font-size: 18px;
  margin-bottom: 10px;
}

.venue-info .day {
  font-weight: bold;
  margin-right: 20px;
}

.venue-info .location {
  color: rgba(255, 255, 255, 0.9);
}

.venue-address {
  font-size: 16px;
  margin-top: 10px;
  color: white;
}

.venue-address a {
  color: white;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}

.venue-address a:hover {
  border-bottom: 1px solid white;
}

.venue-description {
  font-size: 16px;
  opacity: 0.95;
  margin-top: 15px;
}

.loading {
  text-align: center;
  padding: 60px;
  color: #666;
  font-size: 18px;
}

.venue-unavailable {
  text-align: center;
  padding: 48px 20px 60px;
  color: #555;
  max-width: 28rem;
  margin: 0 auto;
}

.venue-unavailable p {
  font-size: 1.1rem;
  line-height: 1.5;
  margin: 0.75rem 0;
}

.no-events {
  text-align: center;
  padding: 60px;
  color: #666;
}

.no-events p {
  font-size: 18px;
  margin: 10px 0;
}

h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

.dates-list {
  list-style: none;
  padding: 0;
  margin: 0 auto 20px;
  width: 420px;
  max-width: 100%;
}

.dates-list li {
  width: 100%;
  display: grid;
  grid-template-columns: 20px 1fr; /* Fixed width for checkbox column */
  gap: 10px;
  align-items: center;
  padding: 12px 0;
}

.dates-list li input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
  justify-self: start; /* Ensure checkbox is left-aligned in its column */
}

.dates-list li label {
  cursor: pointer;
  font-size: 16px;
  margin: 0;
  display: contents;
  justify-self: start;
}

.button-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 30px;
}

button[type="submit"] {
  padding: 15px 40px;
  font-size: 18px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: bold;
}

button[type="submit"]:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

button[type="submit"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.auth-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

@media (max-width: 768px) {
  .venue-header h1 {
    font-size: 24px;
  }

  .venue-info {
    font-size: 14px;
  }

  .dates-list {
    width: 100%;
  }

  .dates-list li {
    padding: 12px 16px;
  }

  button[type="submit"] {
    padding: 12px 30px;
    font-size: 16px;
  }
}
</style>
